import * as React from 'react';
import {
    DefaultButton, Icon, Spinner, SpinnerSize
} from "@fluentui/react";
import { useCategoryMenuVM } from "../../viewmodels/CategoryMenuVM";
import useProductVM from "../../viewmodels/ProductVM";
import { HomePageLinkFilterModel, ProductKeyModel } from "../../models/ProductModel";
import { Utils } from '../../../common/views/Utils';
import { Page } from '../../../grid/models/Page';
import { Pager } from '../../../grid/views/Pager';
import { ProductMenuFilterOption, MenuFilterModel } from '../../models/CategoryModel';
import { UserProductLineService } from '../../services/UserProductLineService';
import { IPictureService } from '../admin/ProductDetailView';
import ProductFilterView from './ProductFilterView';
import ProductGridView from '../shared/ProductGridView';
import ProductFilterTextView from './ProductFilterTextView';
import ProductSearchView from './ProductSearchView';
import ProductDetailWrapperView from './ProductDetailWrapperView';
import { addCartFunc } from '../../../carts/views/CartView';
import useFavoriteVM from '../../../favorites/viewmodels/FavoriteVM';
import useProductAccessoryVM from '../../viewmodels/ProductAccessoryVM';
import TopCategoryMenuView from '../../../home/views/TopCategoryMenuView';
import ShopByMenu from './ShopByMenu';
import BrandView from '../../../brands/views/BrandView';
import productLogo from "../../../../assets/images/products.png";
import filterLogo from "../../../../assets/images/filter.png";

export class UserProductPictureService implements IPictureService {
    private _service = new UserProductLineService();

    public create(): JQueryPromise<any> {
        return this._service.emptyRequest();
    }

    public edit(): JQueryPromise<any> {
        return this._service.emptyRequest();
    }

    public editList(): JQueryPromise<any> {
        return this._service.emptyRequest();
    }

    public getList(ownerId: number): JQueryPromise<any> {
        return this._service.getPictures(ownerId);
    }

    public delete(): JQueryPromise<any> {
        return this._service.emptyRequest();
    }
}

export type ProductViewProps = {
    linkToProductModel: HomePageLinkFilterModel;
    addToCart: addCartFunc;
}

export default function ProductView({ linkToProductModel, addToCart }: ProductViewProps) {
    const pictureService = new UserProductPictureService();

    const hasLoaded = React.useRef(false);
    const vm = useProductVM(linkToProductModel);
    const catVM = useCategoryMenuVM();
    const productAccessoryVM = useProductAccessoryVM();
    const favoriteVM = useFavoriteVM();

    React.useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.onLoad();

        const menuOption: ProductMenuFilterOption = {
            hideBrand: false,
            hideGender: false,
            hideCategory: false
        };

        catVM.getList(menuOption);

        productAccessoryVM.getCategories();
    }, [vm, catVM, productAccessoryVM, linkToProductModel]);

    //const isMobile = window.innerWidth <= 700;
    const isMobile = Utils.isMobile();

    const onShowFilter = () => {
        const count = vm.countDict;
        catVM.open(vm.filter.menuFilters, count);

        vm.tempSetFilter(vm.filter);
        vm.setShowFilter(true);
    };

    const onCloseFilter = () => {
        catVM.close();
        vm.setShowFilter(false);
    }

    const onSubmitFilter = () => {
        const selectedList = catVM.getSelectedList().map(
            (item) => {
                return { type: item.type, value: item.value, text: item.text } as MenuFilterModel;
            });

        const newFilter = vm.tempFilter;

        newFilter.menuFilters = selectedList;
        vm.tempSetFilterField("menuFilters", selectedList);

        vm.getListWithFilter(newFilter);
        onCloseFilter();
    }

    const onDetail = (key: ProductKeyModel) => {
        vm.setSelectedProduct(key);
        vm.setShowDetail(true);
    }

    const onCompletedDetail = () => {
        vm.setShowDetail(false);
    }

    const onPageChange = (page: Page) => {
        if (page) {
            vm.filter.pageNumber = page.pageNumber;
            if (vm.filter.pageSize !== page.pageSize) vm.filter.pageNumber = 1;
            vm.filter.pageSize = page.pageSize;

            vm.getList(true);
        }
    }

    const onSetFavorite = (lineId: number, isDeleted: boolean) => {
        const callback = (succeed: boolean) => {
            if (succeed) {
                vm.setLines(prev =>
                    prev.map(item => item.id === lineId ?
                        { ...item, isFavorite: !item.isFavorite } :
                        item
                    )
                );
            }
        }

        favoriteVM.set(lineId, isDeleted, false, callback);
    }

    const filterTextView = () => {
        return <>
            <ProductFilterTextView
                filter={vm.filter}
                sizeOptions={vm.sizeOptions}
                onChange={vm.getListWithFilter}
            />
        </>;
    }

    const mobileFilterButton = () => {
        return <>
            <DefaultButton
                className={"context-menu-text"}
                onClick={onShowFilter}
                styles={{
                    root: { height: 35, minWidth: 40, width: 40, verticalAlign: "top" },
                    menuIcon: { display: "none !important" }
                }}
                style={{ backgroundColor: "white", height: 32 }}
            >
                <Icon iconName="Filter" />
            </DefaultButton>
        </>;
    }

    const desktopFilterButton = () => {
        return <div
            className="ph-10 pointer container border ml-5 mr-5 mt-5 mb-5"
            onClick={onShowFilter}
            style={{ minWidth: 100, height: 40, display: "inline-flex", borderRadius: "30px" }}
        >
            <img src={filterLogo} alt={"Filters"} width={30} height={30} />
            <span className="font-smaller pl-5">Sorts & Filters</span>
        </div>;
    }

    const allBrandsButton = () => {
        return <div
            className="ph-10 pointer container border ml-5 mr-5 mt-5 mb-5"
            onClick={() => vm.setShowAllBrands(true)}
            style={{ minWidth: 100, height: 40, display: "inline-flex", borderRadius: "30px" }}
        >
            <img src={productLogo} alt={"All Brands"} width={30} height={30} />
            <span className="font-smaller pl-5">All Brands</span>
        </div>;
    }

    const headerDesktopView = () => {
        return <div>
            {productAccessoryVM.categories.length > 0 && <div className="pv-10">
                {productAccessoryVM.filteredCategories(["Men", "Women", "Boys", "Girls", "Clearance"])
                    .map((category, index) => {
                        return <>
                            <TopCategoryMenuView item={category} />
                        </>
                    })}

                {allBrandsButton()}
                {desktopFilterButton()}
            </div>}

            <div className="pv-10 center">
                {filterTextView()}
            </div>
        </div>;
    }

    const headerMobileView = () => {
        const width2 = Utils.getColumnWidth(2);
        const width8 = Utils.getColumnWidth(8);

        return <>
            <div className="ms-Grid-row pl-5 pr-10">
                <div className={width2 + " left"}>
                    <ShopByMenu
                        list={productAccessoryVM.categories}
                        showAllBrands={() => vm.setShowAllBrands(true)}
                    />
                </div>
                <div className={width8}>
                    <ProductSearchView />
                </div>
                <div className={width2 + " right"}>
                    {mobileFilterButton()}
                </div>
            </div>

            <div className="pt-10">{filterTextView()}</div>
        </>;
    }

    return <div>
        {isMobile && headerMobileView()}
        {!isMobile && headerDesktopView()}

        {vm.loading && <div className="pv-10"><Spinner size={SpinnerSize.large} /></div>}

        <ProductGridView
            isAdmin={false}
            isMobile={isMobile}
            lines={vm.lines}
            onDetail={onDetail}
            onBarcode={() => { }}
            addToCart={addToCart}
            setFavorite={onSetFavorite}
        />

        <div style={{ width: "100%", display: "inline-block" }}>
            <Pager
                totalCount={vm.totalCount}
                onChange={onPageChange}
                pageSize={vm.filter.pageSize}
                key={vm.pageHash}
            />
        </div>

        {vm.showFilter && <>
            <ProductFilterView
                filter={vm.tempFilter}
                onValueChange={vm.tempSetFilterField}
                categoryList={catVM.list}
                getSelectedCategoryList={catVM.getSelectedList}
                onCategoryChecked={catVM.setCheck}
                onCategoryCollapsed={catVM.setCollapse}
                sizeOptions={vm.sizeOptions}
                onCancel={onCloseFilter}
                onFilter={onSubmitFilter}
            />
        </>}

        {vm.showDetail && vm.selectedProduct && <>
            <ProductDetailWrapperView
                product={vm.selectedProduct}
                pictureService={pictureService}
                onClose={onCompletedDetail}
                isMobile={isMobile}
                addToCart={addToCart}
                setFavorite={onSetFavorite}
            />
        </>}

        {vm.showAllBrand && <BrandView onClose={() => vm.setShowAllBrands(false)} />}
    </div>;
}